import { FlipHorizontal2, FlipVertical2, Grip, RotateCcw, RotateCw, ZoomIn, ZoomOut } from 'lucide-react'
import {
  MAX_STRAIGHTEN_DEG,
  MIN_STRAIGHTEN_DEG,
  STRAIGHTEN_STEP_DEG,
  integerRectInside,
  orientationCropBounds,
} from '@/core/geometry'
import { useEditSession } from '@/core/store/editSession'
import { selectEffectiveRatio, selectSelectedSource } from '@/core/store/selectors'
import { TEST_IDS } from '@/lib/testIds'
import { useIsCoarsePointer } from '@/lib/usePointerType'
import { setSelectedCropZoom } from '@/tools/crop/actions'
import { cropZoom } from '@/tools/crop/gestures/frameMath'
import { selectSelectedCrop } from '@/tools/crop/selectors'
import {
  FlipAxis,
  QuarterTurnDirection,
  commitSelectedStraighten,
  flipSelectedImage,
  resetSelectedOrientation,
  setSelectedStraighten,
  turnSelectedImage,
} from '@/tools/orientation/actions'
import { selectSelectedOrientation } from '@/tools/orientation/selectors'
import { Button } from '@/ui/shadcn/button'
import { IconSlider } from '@/ui/IconSlider'
import { Toggle } from '@/ui/shadcn/toggle'

export function CropPanel() {
  const state = useEditSession((current) => current)
  const isCoarsePointer = useIsCoarsePointer()
  const crop = selectSelectedCrop(state)
  const source = selectSelectedSource(state)
  const orientation = selectSelectedOrientation(state)
  const ratio = selectEffectiveRatio(state)
  const bounds = integerRectInside(orientationCropBounds(source, orientation))
  const zoom = cropZoom(crop.rect, bounds, ratio)

  const updateStraighten = (value: number) => {
    setSelectedStraighten(value)
  }

  return (
    <section className="space-y-4" aria-labelledby="crop-heading">
      <h2 id="crop-heading" className="font-semibold">Crop</h2>
      <div className="grid grid-cols-4 gap-2">
        <Button type="button" variant="outline" size="icon" aria-label="Rotate left" data-testid={TEST_IDS.rotateLeft} onClick={() => turnSelectedImage(QuarterTurnDirection.Left)}>
          <RotateCcw aria-hidden="true" />
        </Button>
        <Button type="button" variant="outline" size="icon" aria-label="Rotate right" data-testid={TEST_IDS.rotateRight} onClick={() => turnSelectedImage(QuarterTurnDirection.Right)}>
          <RotateCw aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant={orientation.flipHorizontal ? 'secondary' : 'outline'}
          size="icon"
          aria-label="Flip horizontal"
          aria-pressed={orientation.flipHorizontal}
          data-testid={TEST_IDS.flipHorizontal}
          onClick={() => flipSelectedImage(FlipAxis.Horizontal)}
        >
          <FlipHorizontal2 aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant={orientation.flipVertical ? 'secondary' : 'outline'}
          size="icon"
          aria-label="Flip vertical"
          aria-pressed={orientation.flipVertical}
          data-testid={TEST_IDS.flipVertical}
          onClick={() => flipSelectedImage(FlipAxis.Vertical)}
        >
          <FlipVertical2 aria-hidden="true" />
        </Button>
      </div>
      <div className="space-y-4">
        <IconSlider
          id="straighten-slider"
          data-testid={TEST_IDS.straightenSlider}
          min={MIN_STRAIGHTEN_DEG}
          max={MAX_STRAIGHTEN_DEG}
          step={STRAIGHTEN_STEP_DEG}
          value={orientation.straightenDeg}
          startIcon={<RotateCcw className="size-4" />}
          endIcon={<RotateCw className="size-4" />}
          formatValue={(value) => `${value.toFixed(1)}°`}
          showZeroTick
          onValueChange={updateStraighten}
          onValueCommit={commitSelectedStraighten}
          aria-label="Straighten photo"
        />
        <IconSlider
          min={1}
          max={zoom.max}
          step={0.05}
          value={zoom.value}
          startIcon={<ZoomOut className="size-4" />}
          endIcon={<ZoomIn className="size-4" />}
          formatValue={(value) => `${value.toFixed(1)}×`}
          data-testid={TEST_IDS.cropZoom}
          aria-label="Crop photo zoom"
          onValueChange={setSelectedCropZoom}
          onValueCommit={(value) => setSelectedCropZoom(value, true)}
        />
      </div>
      {isCoarsePointer ? (
        <Toggle
          type="button"
          variant="outline"
          pressed={state.handlesVisible}
          onPressedChange={state.setHandlesVisible}
          data-testid={TEST_IDS.cropFineTune}
          className="size-10"
          aria-label="Show fine-tune crop handles"
        >
          <Grip aria-hidden="true" />
        </Toggle>
      ) : null}
      <Button type="button" variant="ghost" size="sm" data-testid={TEST_IDS.orientationReset} onClick={resetSelectedOrientation}>
        Reset
      </Button>
    </section>
  )
}
